import {
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  WidthType,
  TextRun,
  HeadingLevel,
  ExternalHyperlink
} from 'docx';
import { ParsedTableData, getSourcesList, prepareContentForTable } from '../ChatReply/ChatReply';
import { downloadFilenameGenerator, sanitizeHeaderText } from '.';
import LABELS from '../../../ui-constants/en.json';
import AppConst from '../../../ui-constants/appConstants.json';
import { publicSources } from '../../../label.constant';
import { decodeHTML, getResponseSourceType } from '../../../utils/commonUtils';

import {
  IQuery,
  IQueryContent,
  RESPONSE_BY,
  RESPONSE_TYPE,
  ItableCntentProps
} from '../../../models/IQueryType';
export enum BulletPointType {
  hyphen = '-',
  dot = '•',
  number = 'number',
  NA = 'NA'
}
const noBorder = {
  style: BorderStyle.NONE,
  size: 0,
  color: 'ff0000'
};
const singleBorder = {
  style: BorderStyle.SINGLE,
  size: 2,
  color: 'e8e8ee'
};
const noBorders = {
  top: noBorder,
  bottom: noBorder,
  left: noBorder,
  right: noBorder
};

const tableCellStyling = {
  borders: {
    top: singleBorder,
    bottom: singleBorder,
    left: singleBorder,
    right: singleBorder
  },
  shading: {
    color: 'f7f7f8',
    fill: '000000',
    type: ShadingType.SOLID
  }
};
const emptyRow = new TableRow({
  children: [
    new TableCell({
      borders: noBorders,
      children: [new Paragraph('')]
    })
  ]
});
const DOCX_INDENT_WIDTH = 780;
const SPECIAL_INDENT_LEVEL = 4;
// To make bold the first row
let rowIndex = 0;
let isFirstRow = false;
const separatorOnlyRegex = /^\|(?:-+\|)+$/;

// Fixed parseMarkdownLinks function that returns proper hyperlink elements & text
const parseMarkdownLinks = (
  cellText: string,
  isBold: boolean = false
): (TextRun | ExternalHyperlink)[] => {
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let parts: (TextRun | ExternalHyperlink)[] = [];
  let lastIndex = 0;
  let match;
  if (!cellText || cellText.trim().length === 0) {
    return [new TextRun({ text: cellText || '', size: 20, bold: isBold })];
  }
  // Reset regex lastIndex
  regex.lastIndex = 0;
  // Check if there are any markdown links
  const hasLinks = regex.test(cellText);
  regex.lastIndex = 0; // Reset after test
  if (!hasLinks) {
    return [new TextRun({ text: cellText, size: 20, bold: isBold })];
  }
  while ((match = regex.exec(cellText)) !== null) {
    // Add preceding plain text
    if (match.index > lastIndex) {
      const plainText = cellText.substring(lastIndex, match.index);
      if (plainText) {
        parts.push(
          new TextRun({
            text: plainText,
            size: 20
          })
        );
      }
    }
    // Validate and format URL properly
    let url = match[2].trim();
    const linkText = match[1].trim();
    if (url && linkText) {
      url = url.replace(/(https|http)\/\//g, '$1://');
      // Additional URL validation
      try {
        const urlObj = new URL(url);
        // Ensure the protocol is http or https
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
          throw new Error('Invalid protocol');
        }
        // Create ExternalHyperlink for clickable link
        parts.push(
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: linkText,
                size: 20,
                color: '0563c1',
                underline: {}
              })
            ],
            link: url
          })
        );
      } catch (error) {
        // If URL is invalid, treat as plain text
        console.warn(`Invalid URL found: ${url}`, error);
        parts.push(
          new TextRun({
            text: `[${linkText}](${match[2]})`,
            size: 20
          })
        );
      }
    } else {
      // If no valid URL or text, treat as plain text
      parts.push(
        new TextRun({
          text: match[0],
          size: 20
        })
      );
    }
    lastIndex = match.index + match[0].length;
  }
  // Add remaining plain text
  if (lastIndex < cellText.length) {
    const remainingText = cellText.substring(lastIndex);
    if (remainingText) {
      parts.push(
        new TextRun({
          text: remainingText,
          size: 20
        })
      );
    }
  }
  return parts;
};

export const parseMarkdownTableToDocxTable = (
  markdown: string,
  isBold: boolean = false
): Table | null => {
  const line = markdown.trim().split('\n')[0];
  const row = line
    .split('|')
    .map((h) => h.trim())
    .filter(Boolean);
  const cells = row.map((cellText) => {
    const elements = parseMarkdownLinks(cellText, isBold);
    return new TableCell({
      children: [
        new Paragraph({
          children: elements
        })
      ]
    });
  });
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.AUTOFIT,
    rows: [new TableRow({ children: cells })]
  });
};

const isMarkdownTableLine = (line: string) =>
  /^\|(?:\s*[^|\n]+\s*\|)+\s*$/.test(line) && !/^\|\s*-+/.test(line);

const generateTable = (tableContent: ParsedTableData | null) => {
  if (tableContent) {
    const headersCells: TableCell[] = [];
    const valueRows: TableRow[] = [];
    tableContent.headers.forEach((header) =>
      headersCells.push(
        new TableCell({
          ...tableCellStyling,

          children: [
            new Paragraph({
              text: sanitizeHeaderText(header),
              style: 'tableHeader',
              spacing: {
                after: 100
              }
            })
          ]
        })
      )
    );

    tableContent.values.forEach((valArr: any) => {
      const cells: TableCell[] = [];
      valArr.forEach((val: ItableCntentProps) => {
        cells.push(
          new TableCell({
            ...tableCellStyling,
            children: [new Paragraph({ text: `${val.val}`, style: 'summary' })]
          })
        );
      });
      valueRows.push(
        new TableRow({
          children: cells
        })
      );
    });

    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [
        // Header
        new TableRow({
          tableHeader: true,
          children: headersCells
        }),
        ...valueRows
      ]
    });
  } else {
    return new Table({
      rows: [emptyRow]
    });
  }
};
const generateCombinedTable = (tableContent: any, source: string) => {
  if (tableContent?.length && tableContent?.length > 0) {
    const headersCells: TableCell[] = [];
    const valueRows: TableRow[] = [];

    headersCells.push(
      new TableCell({
        ...tableCellStyling,

        children: [
          new Paragraph({
            text: sanitizeHeaderText(
              source === 'OpenAIMR' || source === 'EmbOpenAIMR' || source === 'EmbMR'
                ? 'Document Title'
                : 'Relevant Document(s)'
            ),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      }),
      new TableCell({
        ...tableCellStyling,

        children: [
          new Paragraph({
            text: sanitizeHeaderText(
              source === 'OpenAIMR' || source === 'EmbOpenAIMR' || source === 'EmbMR'
                ? 'Page nos.'
                : 'Author(s)'
            ),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      })
    );
    if (source === 'OpenAIMR' || source === 'EmbOpenAIMR' || source === 'EmbMR') {
      headersCells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({
              text: sanitizeHeaderText('Source'),
              style: 'tableHeader',
              spacing: {
                after: 100
              }
            })
          ]
        })
      );
    }
    tableContent.forEach((data: any) => {
      const cells: TableCell[] = [];
      let fileNameContent = '';
      let fileParts = '';
      let pageNo = '';
      if (data.page_number.length > 0) {
        fileNameContent = data.file_name;
        fileParts = data.page_number.length > 1 ? '(Slides ' : '(Slide ';
      }
      data.page_number.length &&
        data.page_number.forEach((numer: any, i: number) => {
          fileParts = fileParts + numer + (i === data.page_number.length - 1 ? '' : ', ');
          pageNo = pageNo + numer + (i === data.page_number.length - 1 ? '' : ', ');
        });
      fileParts = fileParts + ')';

      cells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({ text: fileNameContent }),
            new Paragraph({
              text:
                source === 'OpenAIMR' || source === 'EmbOpenAIMR' || source === 'EmbMR'
                  ? ''
                  : fileParts
            })
          ]
        })
      );
      if (source === 'OpenAIMR' || source === 'EmbOpenAIMR' || source === 'EmbMR') {
        cells.push(
          new TableCell({
            ...tableCellStyling,
            children: [new Paragraph({ text: pageNo })]
          })
        );
      }
      let authorData: string = data.authors.reduce((str: any, author: any) => {
        if (str === '') {
          str = author?.author_name;
        } else {
          str = str + ', ' + author?.author_name;
        }
        return str;
      }, '');
      cells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({
              text:
                source === 'OpenAIMR' || source === 'EmbOpenAIMR' || source === 'EmbMR'
                  ? data?.source
                  : authorData
            })
          ]
        })
      );
      valueRows.push(
        new TableRow({
          children: cells
        })
      );
    });

    return [
      new Table({
        width: {
          size: 100,
          type: WidthType.PERCENTAGE
        },
        layout: TableLayoutType.AUTOFIT,
        rows: [
          new TableRow({
            tableHeader: true,
            children: headersCells
          }),
          ...valueRows
        ]
      }),
      new Table({
        rows: [emptyRow]
      })
    ];
  } else {
    // Always return an array here
    return [];
  }
};

const seTokenError = (replyData: string) =>
  !!replyData &&
  (replyData.includes(LABELS.TOKEN_ERR) ||
    replyData.includes('https://aka.ms/oai/quotaincrease') ||
    replyData.includes('SQL_ERROR'));

const getSources = (values: { Sources: any; summary: string }) => {
  if (values?.Sources || values.summary?.includes('Information_Sources:')) {
    let sourcesFromChat: string = '';
    if (values.summary?.includes('Information_Sources:')) {
      const dataSplitted = values.summary?.split('Information_Sources:');
      values.summary = dataSplitted[0] as any;
      sourcesFromChat = dataSplitted[1];
    }
    return getSourcesList(sourcesFromChat, values?.Sources || []);
  }
};
const parseFormattedLineChildren = (line: string, isBold = false): (TextRun | ExternalHyperlink)[] => {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  const result: (TextRun | ExternalHyperlink)[] = [];  
  parts.filter(Boolean).forEach((part) => {
    const boldMatch = part.match(/^\*\*(.*)\*\*$/);
    if (boldMatch) {
      result.push(new TextRun({ text: boldMatch[1].replace('-', ''), bold: true }));
    } else {
      let cleanedPart = part.replace(/^•\s*/, '');
      cleanedPart = cleanedPart.startsWith('-') ? cleanedPart.replace(/^-\s?/, '') : cleanedPart;
      cleanedPart = cleanedPart.replace(/^(\d+)[\)\-]\s*/, '$1. ');
      cleanedPart = cleanedPart.replace(/^(\d+\.\s+)-\s*/, '$1');
      const elements = parseMarkdownLinks(cleanedPart, isBold);
      result.push(...elements);
    }
  });  
  return result;
};

const countSpacesBeforeHyphen = (input: string, bulletPointType: BulletPointType): number => {
  let indexOfHyphen = 0;
  if (bulletPointType === BulletPointType.hyphen) {
    const match = input.match(/^(\s*)-/);
    return match ? match[1].length : 0;
  }
  if (bulletPointType === BulletPointType.dot) {
    indexOfHyphen = input.indexOf('•');
  }

  if (bulletPointType === BulletPointType.number) {
    const match = input.match(/^(\s*)/);
    return match ? match[1].length : 0;
  }
  const substringBeforeHyphen = input.slice(0, indexOfHyphen);
  const spaceCount = (substringBeforeHyphen.match(/ /g) || []).length;
  return spaceCount;
};

const generateDocSummary = (
  line: string,
  type: string,
  totalLength: number = 0,
  ind: number,
  isParitallyAbortedResponse: boolean,
  isCompletelyAbortedResposne: boolean,
  selectedSources?: any
) => {
  let modifiedSource: any = [];
  if (isParitallyAbortedResponse) {
    modifiedSource.push(
      new TextRun({
        text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
        break: 1,
        color: '656579',
        italics: true
      })
    );
  }

  if (seTokenError(line)) {
    return new Paragraph({
      children: [new TextRun({ text: LABELS.chat_reply.EMBROYNIC_ERROR })]
    });
  }

  if (line.startsWith(LABELS.SORRY_ERR) && !line.startsWith(LABELS.SORRY_ERR1)) {
    return new Paragraph({
      children: [
        new TextRun({ text: LABELS.GENERIC_OOPS_ERROR }),
        new TextRun({
          text: ' Source: Secondary research',
          break: 1
        })
      ]
    });
  }

  if (RESPONSE_BY.OPENAI === type && line?.includes('Information_Sources')) {
    return new Paragraph(line?.replace('Information_Sources', 'Source: Secondary research'));
  }

  if (RESPONSE_BY.OPENAI === type && line?.includes('Oops! No results found')) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        new TextRun({
          text: 'Source: Secondary research',
          break: 1
        })
      ]
    });
  }
  const isNumericBullet = (sentence: string): boolean => {
    const numericBulletPattern = /^\s*\d+[\.\)\-]\s+/;
    return numericBulletPattern.test(sentence);
  };

  const content = line.trim();
  // Handle Markdown Table
  if (isFirstRow && content === '') {
    rowIndex = 0;
    isFirstRow = false;
  }
  const selectedSourceList = selectedSources?.split(',').map((source: string) => source.trim());
  if (isMarkdownTableLine(content) && Array.isArray(selectedSourceList)) {
    const tableLines: string[] = [content];
    for (let i = ind + 1; i < totalLength; i++) {
      const next = selectedSourceList[i]?.trim?.();
      if (isMarkdownTableLine(next)) {
        tableLines.push(next);
      } else {
        break;
      }
    }
    rowIndex++;
    const markdownBlock = tableLines.join('\n');
    const parsedTable = parseMarkdownTableToDocxTable(markdownBlock, rowIndex === 1 && !isFirstRow);
    if (parsedTable) return parsedTable;
  }

  if (separatorOnlyRegex.test(content)) {
    isFirstRow = true;
    return [];
  }
  const headerMatch = content.match(/^(#+)\s+(.*)/);
  let currentIndentLevel = 0;
  let isNumericbulletpoint = isNumericBullet(line);
  let startsWithHyphen = line.trim().startsWith('-');

  if (isNumericbulletpoint) {
    currentIndentLevel = countSpacesBeforeHyphen(line, BulletPointType.number);
  } else if (startsWithHyphen) {
    currentIndentLevel = countSpacesBeforeHyphen(line, BulletPointType.hyphen);
  } else {
    currentIndentLevel = -1;
  }

  if (headerMatch) {
    if (ind !== 0) {
      return [
        new Paragraph({
          children: [new TextRun({ text: headerMatch[2].replace('-', ''), bold: true })]
        }),
        // Only this paragraph will add a space
        new Paragraph({ text: '' })
      ];
    } else {
      // No extra space
      return [
        new Paragraph({
          children: [new TextRun({ text: headerMatch[2].replace('-', ''), bold: true })]
        })
      ];
    }
  }
  if (RESPONSE_BY.STRATEGY_EDGE === type && totalLength - 1 === ind) {
    return new Paragraph({
      children: [
        new TextRun({
          text: line,
          color: isCompletelyAbortedResposne ? '656579' : '',
          italics: isCompletelyAbortedResposne ? true : false
        }),
        // new TextRun({ text: '', break: 1 }),
        ...modifiedSource
      ]
    });
  }
  if (content.trim().length === 0) {
    return new Paragraph({
      children: parseFormattedLineChildren(content)
    });
  } else if (currentIndentLevel === -1) {
    return new Paragraph({
      children: parseFormattedLineChildren(content)
    });
  } else {
    // Don't apply bullet formatting if content starts with a number (numeric list)
    const hasNumericPrefix = /^\d+[\.\)\-]\s/.test(content);
    
    if (hasNumericPrefix) {
      return new Paragraph({
        indent: {
          left: 360 // Add tab indentation for numbered items
        },
        children: parseFormattedLineChildren(content)
      });
    }
    
    let bullet;
    switch (currentIndentLevel) {
      case 3:
        bullet = { level: 2 };
        break;
      case 4:
      case 5:
        bullet = { level: 3 };
        break;
      case 2:
        bullet = { level: 1 };
        break;
      default:
        bullet = { level: 0 };
    }
              
    return new Paragraph({
      bullet,
      indent: {
        left:
          currentIndentLevel === SPECIAL_INDENT_LEVEL
            ? currentIndentLevel * DOCX_INDENT_WIDTH
            : undefined
      },
      children: parseFormattedLineChildren(content)
    });
  }
};
const generateDocument = async (
  data: any,
  userImg: string | undefined,
  source: any,
  checkedCheckboxes: any
) => {
  const IMAGES = {
    [RESPONSE_BY.USER]: userImg ? userImg : AppConst.AVATAR_IMAGE,
    [RESPONSE_BY.STRATEGY_EDGE]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBAI]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBROYNIC]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.OPENAI]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBAIMR]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.AIMR]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBMR]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.MARKET_REPORT]: AppConst.USER_AI_IMG
  };
  const paragraphs: any = [];
  const handleSourceSection = () => {
    let sourceType = '';
    if (checkedCheckboxes && checkedCheckboxes.length > 0) {
      const [embryonic, openAI, marketReports] = publicSources;
      const hasEmbryonic = checkedCheckboxes.includes(embryonic);
      const hasOpenAI = checkedCheckboxes.includes(openAI);
      const hasMarket = checkedCheckboxes.includes(marketReports);

      switch (checkedCheckboxes.length) {
        case 1:
          if (hasMarket) sourceType = marketReports;
          else if (hasEmbryonic) sourceType = embryonic;
          else if (hasOpenAI) sourceType = openAI;
          break;
        case 2:
          if (hasMarket && hasEmbryonic) sourceType = 'WithEmbMR';
          else if (hasMarket && hasOpenAI) sourceType = 'WithOAIMR';
          else if (hasEmbryonic && hasOpenAI) sourceType = 'Combined';
          break;
        case 3:
          if (hasMarket && hasEmbryonic && hasOpenAI) sourceType = 'WithMR';
          break;
        default:
          break;
      }
    }
    return getResponseSourceType(sourceType);
  };

  const tableRowWithCol = (
    respData: any,
    respImg: any,
    tableContent?: any,
    respType?: any,
    embTableContent?: ParsedTableData | null | undefined,
    selectedSources?: string | any[] | undefined
  ) => {
    const totalLength = decodeHTML(respData.summary).split('\n');
    const { length } = totalLength;
    return paragraphs.push(
      new TableRow({
        children: [
          new TableCell({
            borders: noBorders,
            margins: {
              right: 200
            },
            children: [
              new Paragraph({
                children: [
                  new ImageRun({
                    data: respImg,
                    transformation: {
                      width: 30,
                      height: 30
                    }
                  })
                ]
              })
            ]
          }),

          new TableCell({
            borders: noBorders,
            children: [
              ...(respType !== 'user'
                ? decodeHTML(respData.summary)
                    .split('\n')
                    .flatMap((line: string, ind: number) => {
                      const res = generateDocSummary(
                        line,
                        respType,
                        length,
                        ind,
                        respData?.isParitallyAbortedResponse,
                        respData?.isCompletelyAbortedResposne,
                        selectedSources
                      );
                      return Array.isArray(res) ? res : [res];
                    })
                : [new Paragraph(respData.summary?.replace(/\n\r+$/, '').trim())]),
              generateTable(embTableContent ?? null),
              ...(embTableContent && embTableContent.headers && embTableContent.headers.length > 0
                ? [new Paragraph({ text: '' })]
                : []),
              ...generateCombinedTable(tableContent, source),
              ...(selectedSources && selectedSources.length > 0
                ? [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Source: ${selectedSources}`
                        })
                      ]
                    }),
                    new Paragraph({ text: '' })
                  ]
                : [])
            ]
          })
        ]
      })
    );
  };

  data.forEach(async (chat: any, ind: number) => {
    rowIndex = 0;
    isFirstRow = false;
    const { SEResponse, LLMResponse, user } = chat;
    let tableContent = null;
    let sources: string[] | undefined = [];
    let embTableContent = null;
    if (
      SEResponse?.ResponseType === RESPONSE_TYPE.TABLE &&
      SEResponse.Content &&
      SEResponse.Content.every((el: any) => Boolean(el) && typeof el === 'object')
    ) {
      embTableContent = prepareContentForTable(SEResponse?.Content || LLMResponse?.Content);
    }
    if (
      SEResponse?.relevantFiles &&
      SEResponse?.relevantFiles.every((el: any) => Boolean(el) && typeof el === 'object')
    ) {
      tableContent = SEResponse?.relevantFiles?.map((el: any) => {
        const obj: any = {
          file_name: el.file_name ?? '',
          page_number: el.page_number || [],
          authors: el?.authors || [],
          sector: el?.sector || [],
          geography: el?.geography || [],
          contentAge: el?.contentAge || '',
          source: el?.source || ''
        };
        return obj;
      });
    }
    if (SEResponse || LLMResponse) {
      sources = getSources(SEResponse || LLMResponse);
    }

    if (user) {
      tableRowWithCol(user, IMAGES[RESPONSE_BY.USER], null, RESPONSE_BY.USER);
    }
    if (SEResponse) {
      if (source === 'OpenAIMR' || source === 'EmbOpenAIMR' || source === 'EmbMR') {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.EMBAI],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          embTableContent,
          handleSourceSection()
        );
      }
    }
  });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            size: '11pt',
            font: 'Calibri'
          }
        }
      },
      paragraphStyles: [
        {
          id: 'tableHeader',
          run: {
            bold: true
          }
        },
        {
          id: 'summary',
          quickFormat: true
        }
      ]
    },
    sections: [
      {
        children: [
          new Table({
            rows: paragraphs
          })
        ]
      }
    ]
  });
  return doc;
};

const exportToCombinedDoc = async (
  data: IQuery[],
  userImage?: string,
  source?: any,
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  const clonedData = structuredClone(data);
  const doc = await generateDocument(clonedData, userImage, source, checkedCheckboxes);
  const blob = await Packer.toBlob(doc);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = downloadFilenameGenerator(source, 'docx');
  link.click();
};

export default exportToCombinedDoc;
